import { TrashIcon } from "@heroicons/react/24/outline";
import { generatePath, NavLink } from "react-router-dom";

import Card from "~/components/Card/Card.tsx";
import CardValueFullWidthWithOptionalBorder
	from "~/components/Card/components/CardValueFullWidthWithOptionalBorder.tsx";
import Headline from "~/components/Headline/Headline.tsx";
import StatusBadge from "~/components/StatusBadge/StatusBadge.tsx";
import Tooltip from "~/components/Tooltip.tsx";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import {
	getInvoiceStatusDisplayNameById,
	getInvoiceStatusThemeById,
} from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import {
	getAttachedInvoiceExcessCents,
	getInvoiceDetachLockMessage,
} from "~/modules/quota/utils/quotaInvoiceUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	onAttachClick: () => void;
	onDetachClick: (invoice: Invoice) => void;
	quota: QuotaWithRelations;
};

// The invoice card of the quota details view. An advance quota shows its advance invoice.
// An invoice-backed quota lists its attached invoices with their excess, and attaches and detaches them.
export const QuotaInvoicesCard = ({ onAttachClick, onDetachClick, quota }: Props) => {
	const isInvoiceBacked = quota.type === "invoice-backed";

	return <Card title={isInvoiceBacked ? "Rechnungen" : "Vorauszahlungsrechnung"}
				 headerButtonText={isInvoiceBacked ? "Rechnungen hinzufügen" : undefined}
				 onHeaderButtonClick={isInvoiceBacked ? onAttachClick : undefined}>
		<div className="mt-2 space-y-4">
			{quota.invoices.length === 0 &&
				<div className="text-sm text-gray-500">
					{isInvoiceBacked ? "Keine Rechnungen zugewiesen." : "Keine Rechnungen vorhanden."}
				</div>}
			{quota.invoices.map((invoice) => {
				const invoiceLink = <NavLink to={generatePath(INVOICE_DETAILS_PATH, { invoiceId: invoice.id })}
											 className="group flex grow items-center justify-between gap-x-4 rounded-lg border border-gray-200 px-4 hover:bg-gray-100">
					<div>
						<Headline type="h5"
								  color="muted">{invoice.invoiceNumber ?? EMPTY_VALUE_PLACEHOLDER}</Headline>
						<div className="text-sm text-gray-500">{invoice.projectTitle ?? EMPTY_VALUE_PLACEHOLDER}</div>
					</div>
					<div className="ml-auto" />
					<div>
						<CardValueFullWidthWithOptionalBorder label="Rechnungsbetrag (netto)"
															  hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(invoice.subtotalCents)}</span></CardValueFullWidthWithOptionalBorder>
					</div>
					{isInvoiceBacked && <div>
						<CardValueFullWidthWithOptionalBorder label="Überschuss"
															  hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(getAttachedInvoiceExcessCents(invoice))}</span></CardValueFullWidthWithOptionalBorder>
					</div>}
					<StatusBadge theme={getInvoiceStatusThemeById(invoice.invoiceStatusId)}>{getInvoiceStatusDisplayNameById(invoice.invoiceStatusId)}</StatusBadge>
				</NavLink>;

				if (!isInvoiceBacked) {
					return <div key={invoice.id}
								className="flex">{invoiceLink}</div>;
				}

				const detachLockMessage = getInvoiceDetachLockMessage(quota, invoice.id);
				const detachButton = <button type="button"
											 className="group flex flex-col items-center justify-center disabled:cursor-not-allowed"
											 disabled={!!detachLockMessage}
											 onClick={() => onDetachClick(invoice)}>
					<TrashIcon className={detachLockMessage
						? "size-5 text-gray-300"
						: "size-5 text-danger-500 group-hover:text-danger-700"} />
				</button>;

				return <div key={invoice.id}
							className="flex items-center gap-x-4">
					{invoiceLink}
					{detachLockMessage ?
						// A disabled button fires no mouse events, so the wrapper receives the hover.
						<Tooltip text={detachLockMessage}>
							<span>{detachButton}</span>
						</Tooltip> :
						detachButton}
				</div>;
			})}
		</div>
	</Card>;
};
